import { DeletePaper } from '@src/queries/DeletePaper'
import { GetPapers } from '@src/queries/GetPapers'
import { DELETE_PAPER, GET_PAPERS } from '@src/queries/Papers'
import {
  deletePaper,
  DeletePaperPayload,
  getPapers,
  getPapersError,
  setPapers,
  transferPapers,
  TransferPapersPayload,
} from '@src/state/papers/actions'
import { RootState } from '@src/state/types'
import { setPaperUploadedCookie } from '@src/utils/cookies'
import graphApi from '@src/utils/http-clients/graphApi'
import { getWFELS } from '@src/utils/localStorage'
import { isWorkspacePage } from '@src/utils/routes'
import { convertFromRaw, EditorState } from 'draft-js'
import { createLogic } from 'redux-logic'
import { getType } from 'typesafe-actions'
import { getPaidMember } from '../auth/selectors'
import {
  createPaper,
  loadModules,
  setComments,
  showGatedComments,
} from '../document/actions'
import { BLOCK_KEY } from '../document/document.helper'
import { migrateComments, migrateEditorState } from '../document/helpers/draftjs.helper'
import { Paper } from './reducer'

export const wtaiGetPapersLogic = createLogic<RootState>({
  type: getType(getPapers),
  async process({ getState }, dispatch, done) {
    try {
      const { data } = await graphApi.query<GetPapers>({
        query: GET_PAPERS,
        fetchPolicy: 'network-only',
      })
      const papers: Paper[] = data.getPapers.map(
        ({ updatedAt, filename, uploadUuid }) => ({
          updatedAt,
          filename: filename || '',
          id: uploadUuid,
        })
      )
      dispatch(setPapers({ papers }))

      if (papers.length) {
        const {
          site,
          auth: { jwt },
        } = getState()
        jwt && setPaperUploadedCookie(site, jwt)
      }
    } catch (error) {
      const papersError = new Error('GET_PAPERS_FAIL')
      papersError.stack = error
      dispatch(getPapersError({ error: papersError }))
    }
    done()
  },
})

export const wtaiDeletePaperLogic = createLogic<RootState, DeletePaperPayload>({
  type: getType(deletePaper.request),
  validate({ getState, action }, allow, reject) {
    const {
      papers: { deleting },
    } = getState()

    if (deleting) {
      return reject(action)
    }

    return allow(action)
  },
  async process({ action }, dispatch, done) {
    const { id } = action.payload
    try {
      await graphApi.mutate<DeletePaper>({
        mutation: DELETE_PAPER,
        variables: {
          uuid: id,
        },
      })
      dispatch(deletePaper.success({}))
      dispatch(getPapers())
    } catch (error) {
      const papersError = new Error('DELETE_PAPERS_FAIL')
      papersError.stack = error
      dispatch(deletePaper.failure(papersError))
    }
    done()
  },
})

export const wtaiTransferPapersLogic = createLogic<RootState, TransferPapersPayload>({
  type: getType(transferPapers),
  validate({ getState, action }, allow, reject) {
    const { router } = getState()
    if (!isWorkspacePage(router?.location.pathname)) {
      return reject(action)
    }
    return allow(action)
  },
  process({ getState }, dispatch, done) {
    const state = getState()
    const isPaid = getPaidMember(state)
    const wfeLs = getWFELS()
    if (wfeLs) {
      const editorState = migrateEditorState({
        editorState: EditorState.createWithContent(convertFromRaw(wfeLs.rawContentState)),
        blockVersion: BLOCK_KEY,
      })
      // TODO: WTP-1695 to unify loading from persistence layer
      const newComments = migrateComments(
        editorState.getCurrentContent(),
        state.document.comments
      )
      dispatch(setComments(newComments))
      // TODO: WTP-1693 - combine loadModules to always run with setEditorState
      dispatch(loadModules({ editorState }))
      dispatch(createPaper({ editorState }))
      if (isWorkspacePage(state.router?.location.pathname) && isPaid) {
        dispatch(showGatedComments({ editorState }))
      }
    }
    done()
  },
})

export const papersLogic = [
  wtaiGetPapersLogic,
  wtaiTransferPapersLogic,
  wtaiDeletePaperLogic,
]



// WEBPACK FOOTER //
// ./src/state/papers/logic.ts